/**
 * LCC2 index (XGRIDS whitepaper v0.0.3): parse the node tree into per-file plans
 * and write the index back with the counts of an export. Editor-independent.
 * https://github.com/xgrids/LCC2Whitepaper
 */

type Vec3Tuple = [number, number, number];
type Lcc2Box = { min: Vec3Tuple, max: Vec3Tuple };

type Lcc2NodeJson = {
    id?: string;
    boundingBox?: Lcc2Box;
    child?: Record<string, Lcc2NodeJson> | Lcc2NodeJson[];
    data?: Record<string, any>;
    [key: string]: unknown;
};

type Lcc2Json = {
    totalSplats: number;
    lodSplats: number[];
    totalLevels: number;
    description?: string;
    env?: { splatsCount?: number, boundingBox?: Lcc2Box, [key: string]: unknown };
    root: Lcc2NodeJson & { splatFiles: string[], meshFiles?: string[], bvhFiles?: string[] };
    [key: string]: unknown;
};

// `path` is the chain of child keys from the root, so a cloned tree can be addressed
type Lcc2Node = { path: string[], level: number, file: number, start: number, count: number };

// one splat file; its nodes are contiguous blocks sorted by start. streamOffset is
// where the file begins in its level's row stream, which splat-transform builds
// from the level's files in ascending file index
type Lcc2File = { index: number, path: string, level: number, nodes: Lcc2Node[], count: number, streamOffset: number };

type Lcc2Index = { json: Lcc2Json, files: Lcc2File[], levels: Lcc2File[][], envFile: number | null };

type NodeResult = { count: number, box: Lcc2Box | null };

// per file: one result per node, parallel to Lcc2File.nodes
type Lcc2WriteResult = { files: Map<number, NodeResult[]>, env: NodeResult | null };

const LCC2_NOTICE = 'Data Organization Format originated from XGRIDS. Modified with Splatfield Editor. Format: https://github.com/xgrids/LCC2Whitepaper';

const children = (node: Lcc2NodeJson): [string, Lcc2NodeJson][] => {
    if (!node.child) return [];
    return Array.isArray(node.child) ? node.child.map((c, i): [string, Lcc2NodeJson] => [String(i), c]) : Object.entries(node.child);
};

const nodeAt = (root: Lcc2NodeJson, path: string[]) => {
    let node = root;
    for (const key of path) {
        node = (node.child as Record<string, Lcc2NodeJson>)[key];
    }
    return node;
};

// XGRIDS writes trailing commas in some indices; splat-transform tolerates them too
const parseJson = (text: string) => {
    try {
        return JSON.parse(text);
    } catch {
        return JSON.parse(text.replace(/,\s*([}\]])/g, '$1'));
    }
};

const parseLcc2Index = (text: string): Lcc2Index => {
    const json = parseJson(text) as Lcc2Json;
    if (typeof json?.totalLevels !== 'number' || !Array.isArray(json.lodSplats) || !Array.isArray(json.root?.splatFiles)) {
        throw new Error('Unsupported LCC2 index (only the LCC2 v0.0.3 protocol is supported)');
    }

    const { totalLevels, root } = json;
    const envFile = typeof root.data?.env?.name === 'number' ? root.data.env.name : null;
    const byFile = new Map<number, Lcc2Node[]>();

    const walk = (node: Lcc2NodeJson, path: string[], depth: number) => {
        for (const [key, child] of children(node)) {
            const childPath = [...path, key];
            const splats = child.data?.['3dgs'];
            if (splats && splats.name !== envFile) {
                if (typeof splats.count !== 'number') {
                    throw new Error(`LCC2 node ${child.id ?? childPath.join('/')} has no splat count`);
                }
                const list = byFile.get(splats.name) ?? [];
                list.push({ path: childPath, level: totalLevels - depth, file: splats.name, start: splats.start ?? 0, count: splats.count });
                byFile.set(splats.name, list);
            }
            walk(child, childPath, depth + 1);
        }
    };
    walk(root, [], 1);

    const files: Lcc2File[] = [...byFile.keys()].sort((a, b) => a - b).map((index) => {
        const path = root.splatFiles[index];
        if (!path) throw new Error(`LCC2 node references missing file ${index}`);
        const nodes = byFile.get(index).sort((a, b) => a.start - b.start);
        const { level } = nodes[0];
        let count = 0;
        for (const node of nodes) {
            if (node.level !== level) throw new Error(`LCC2 file ${path} mixes LOD levels`);
            if (node.start !== count) throw new Error(`LCC2 file ${path} has non-contiguous nodes`);
            count += node.count;
        }
        return { index, path, level, nodes, count, streamOffset: 0 };
    });

    const levels: Lcc2File[][] = Array.from({ length: totalLevels }, (): Lcc2File[] => []);
    for (const file of files) {
        if (file.level < 0 || file.level >= totalLevels) throw new Error(`LCC2 file ${file.path} is outside the LOD range`);
        const list = levels[file.level];
        file.streamOffset = list.reduce((sum, f) => sum + f.count, 0);
        list.push(file);
    }
    levels.forEach((list, level) => {
        const sum = list.reduce((s, f) => s + f.count, 0);
        if (sum !== json.lodSplats[level]) {
            throw new Error(`LCC2 LOD ${level} count mismatch: nodes ${sum}, index ${json.lodSplats[level]}`);
        }
    });

    return { json, files, levels, envFile };
};

const unionBox = (a: Lcc2Box | null, b: Lcc2Box | null): Lcc2Box | null => {
    if (!a) return b;
    if (!b) return a;
    return {
        min: [Math.min(a.min[0], b.min[0]), Math.min(a.min[1], b.min[1]), Math.min(a.min[2], b.min[2])],
        max: [Math.max(a.max[0], b.max[0]), Math.max(a.max[1], b.max[1]), Math.max(a.max[2], b.max[2])]
    };
};

const serializeLcc2Index = (index: Lcc2Index, result: Lcc2WriteResult): string => {
    const json = structuredClone(index.json);
    const { root } = json;

    // writeSog cannot encode an empty file, so files without splats are dropped
    const dropped = new Set(index.files
    .filter(file => (result.files.get(file.index) ?? []).every(node => node.count === 0))
    .map(file => file.index));

    const remap = new Map<number, number>();
    const splatFiles: string[] = [];
    root.splatFiles.forEach((path, i) => {
        if (!dropped.has(i)) {
            remap.set(i, splatFiles.length);
            splatFiles.push(path);
        }
    });
    root.splatFiles = splatFiles;

    const lodSplats = new Array<number>(json.totalLevels).fill(0);
    let rootBox: Lcc2Box | null = null;

    for (const file of index.files) {
        const nodeResults = result.files.get(file.index);
        if (!nodeResults || nodeResults.length !== file.nodes.length) {
            throw new Error(`Missing export result for ${file.path}`);
        }
        let start = 0;
        for (let i = 0; i < file.nodes.length; ++i) {
            const target = nodeAt(root, file.nodes[i].path);
            if (dropped.has(file.index)) {
                delete target.data['3dgs'];
                continue;
            }
            const { count, box } = nodeResults[i];
            target.data['3dgs'] = { ...target.data['3dgs'], name: remap.get(file.index), start, count };
            if (box) target.boundingBox = box;
            start += count;
            lodSplats[file.level] += count;
            rootBox = unionBox(rootBox, box);
        }
    }

    // collision meshes are not exported (decision 14.09.2026)
    const stripCollision = (node: Lcc2NodeJson) => {
        if (node.data) {
            delete node.data.mesh;
            delete node.data.bvh;
        }
        children(node).forEach(([, child]) => stripCollision(child));
    };
    stripCollision(root);
    delete root.meshFiles;
    delete root.bvhFiles;

    json.lodSplats = lodSplats;
    json.totalSplats = lodSplats.reduce((sum, n) => sum + n, 0);
    if (rootBox) root.boundingBox = rootBox;

    if (index.envFile !== null && root.data?.env) {
        root.data.env.name = remap.get(index.envFile);
        if (result.env && json.env) {
            json.env.splatsCount = result.env.count;
            if (result.env.box) json.env.boundingBox = result.env.box;
        }
    }

    json.description = json.description ? `${json.description} | ${LCC2_NOTICE}` : LCC2_NOTICE;

    return JSON.stringify(json, null, 4);
};

export {
    LCC2_NOTICE,
    parseLcc2Index,
    serializeLcc2Index,
    type Lcc2Box,
    type Lcc2File,
    type Lcc2Index,
    type Lcc2Json,
    type Lcc2Node,
    type Lcc2WriteResult,
    type NodeResult
};
